import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import * as RoutingMap from '@config/routing-map';
import { combineLatest } from 'rxjs';
import {
  componentDestroyed,
  Game,
  handleError,
  HockeyGameApi,
  TournamentTeam,
  UntilDestroy
} from '@mtgame/core';
import { LoaderService } from '@mtgame/ui';
import { getTeamPageRoute } from '@config/routes';
import { GamesHeadlineService } from '../../shared/games-headline/services/games-headline.service';
import { LeagueRootService } from '@core/services/league-root.service';
import { GameRootService } from '@core/services/game-root.service';

@UntilDestroy
@Component({
  selector: 'mtg-tournament-game',
  templateUrl: './tournament-game.component.html',
  styleUrls: ['./tournament-game.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TournamentGameComponent implements OnInit, OnDestroy {
  game: Game;
  team: TournamentTeam;
  competitorTeam: TournamentTeam;
  league = this.leagueRootService.getLeagueModel();

  constructor(
    private leagueRootService: LeagueRootService,
    private gameRootService: GameRootService,
    private hockeyGameApi: HockeyGameApi,
    private loaderService: LoaderService,
    private gamesHeadlineService: GamesHeadlineService,
    private cdr: ChangeDetectorRef,
  ) {
  }

  ngOnInit(): void {
    combineLatest([
      this.gameRootService.game$.pipe(takeUntil(componentDestroyed(this)))
    ]).subscribe(([game]) => {
      this.game = game;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.gameRootService.dispose();
  }

  getTeamProfileRoute(teamId: number): string[] {
    return getTeamPageRoute(teamId);
  }

  async downloadGameAppl(type: 'game_user' | 'tournament_user'): Promise<void> {
    this.loaderService.show();
    try {
      const response = await this.hockeyGameApi.downloadApplication(this.game.id, 'pdf', type);
      const a = document.createElement('a');
      const url = window.URL.createObjectURL(response);
      a.href = url;
      a.download = `Заявка на игру ${this.game.team.name} - ${this.game.competitorTeam.name}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      handleError(e);
    } finally {
      this.loaderService.hide();
    }
  }

  printApplication(type: 'game_user' | 'tournament_user'): void {
    window.open(`https://mtgame.ru/api/v1/tournament_hockey_game/${this.game.id}/game_application_file/?file_type=html&application_type=${type}`)
  }

  printProtocol(): void {
    window.open(`https://mtgame.ru/api/v1/tournament_hockey_game/${this.game.id}/game_protocol/?file_type=html`);
  }

  async downloadGameProtocol(): Promise<void> {
    this.loaderService.show();
    try {
      const response = await this.hockeyGameApi.downloadProtocol(this.game.id, 'pdf');
      const a = document.createElement('a');
      const url = window.URL.createObjectURL(response);
      a.href = url;
      a.download = `Протокол игры ${this.game.team.name} - ${this.game.competitorTeam.name}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      handleError(e);
    } finally {
      this.loaderService.hide();
    }
  }

  get teamRoute(): string[] {
    return this.getTeamProfileRoute(this.game.team.id);
  }

  get competitorTeamRoute(): string[] {
    return this.getTeamProfileRoute(this.game.competitorTeam.id);
  }

  get gameStatisticRoute(): string[] {
    return [RoutingMap.ROUTE_GAME_STATISTIC_ROOT];
  }

  get gameProtocolRoute(): string[] {
    return [RoutingMap.ROUTE_GAME_PROTOCOL_ROOT];
  }

  get gameTimelineRoute(): string[] {
    return [RoutingMap.ROUTE_GAME_TIMELINE_ROOT];
  }

  get gameMediaRoute(): string[] {
    return [RoutingMap.ROUTE_GAME_MEDIA_ROOT];
  }

  get gameLiveRoute(): string[] {
    return [RoutingMap.ROUTE_GAME_LIVE];
  }
}
